import { useState, type ReactElement } from 'react';

import { useScimProvisioning } from '@/hooks/api/useScimProvisioningApi';
import type {
  GrantedIntegrationAccess,
  RenderableIntegrationAccess,
} from '@/hooks/useIntegrationAccess';

import { ScimIntegrationCard } from './ScimIntegrationCard';
import { ScimProvisioningModal } from './ScimProvisioningModal';

interface ScimIntegrationProps {
  access: RenderableIntegrationAccess;
}

/**
 * SCIM provisioning integration (KIR-4693) for Settings → Integrations →
 * Identity & Access. Renders the integration tile + a management modal, matching
 * the SAML SSO / SIEM integration pattern.
 */
export function ScimIntegration({
  access,
}: ScimIntegrationProps): ReactElement {
  if (access.canRender) return <ScimIntegrationInner access={access} />;
  return <ScimIntegrationCard connected={false} isLoading />;
}

ScimIntegration.displayName = 'ScimIntegration';

function ScimIntegrationInner({
  access,
}: {
  access: GrantedIntegrationAccess;
}): ReactElement {
  const { organizationId, canWrite, isLoading: isAccessLoading } = access;
  const { data, isLoading: scimLoading } = useScimProvisioning(
    organizationId,
    true
  );
  const [open, setOpen] = useState(false);

  const credential = data?.credential ?? null;
  const connected = credential !== null;
  const openModal = (): void => setOpen(true);

  return (
    <>
      {/* Card actions stay enabled for support (read-scope) — matching SIEM,
          the modal itself enforces write-scope via canManage. */}
      <ScimIntegrationCard
        connected={connected}
        connectionSummary={credential?.name ?? null}
        onConnect={openModal}
        onManage={openModal}
        isLoading={isAccessLoading || scimLoading}
      />
      <ScimProvisioningModal
        // Remount on org switch so a previous tenant's one-time bearer can't
        // linger on screen while generate/disconnect target the new org
        // (matches SIEM / SAML SSO). KIR-4693.
        key={organizationId}
        organizationId={organizationId}
        open={open}
        onOpenChange={setOpen}
        canManage={canWrite}
      />
    </>
  );
}

ScimIntegrationInner.displayName = 'ScimIntegrationInner';
